import { createClient, SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

let supabaseClient: SupabaseClient;

if (!supabaseUrl || !supabaseAnonKey) {
  const missing = [];
  if (!supabaseUrl) missing.push('REACT_APP_SUPABASE_URL');
  if (!supabaseAnonKey) missing.push('REACT_APP_SUPABASE_ANON_KEY');

  console.error(`CRITICAL ERROR: Missing environment variable(s): ${missing.join(', ')}. 
These must be defined in your Vercel Project Settings for the production build to work.
Please add them and trigger a new deployment.`);
  
  // Create a dummy client to prevent downstream crashes, but functionality will fail
  // We cast to any then to SupabaseClient to satisfy the type checker for the dummy
  supabaseClient = {
    from: () => ({ 
      select: () => ({ 
        eq: () => ({ 
          maybeSingle: () => Promise.resolve({ data: null, error: { message: 'Supabase not initialized' } }),
          order: () => ({ maybeSingle: () => Promise.resolve({ data: null, error: { message: 'Supabase not initialized' } }) }),
          in: () => ({ order: () => Promise.resolve({ data: null, error: { message: 'Supabase not initialized' } }) })
        }),
        order: () => ({ eq: () => ({ maybeSingle: () => Promise.resolve({ data: null, error: { message: 'Supabase not initialized' } }) }) }),
        in: () => ({ eq: () => Promise.resolve({ data: null, error: { message: 'Supabase not initialized' } }) })
      }) 
    }),
    auth: {
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      refreshSession: () => Promise.resolve({ data: { session: null, user: null }, error: null }),
      signInWithPassword: () => Promise.resolve({ data: { user: null, session: null }, error: { message: 'Supabase not initialized: Missing environment variables' } }),
      signInWithOtp: () => Promise.resolve({ data: { user: null, session: null }, error: { message: 'Supabase not initialized' } }),
      signInWithOAuth: () => Promise.resolve({ data: { provider: '', url: '' }, error: { message: 'Supabase not initialized' } }),
      signUp: () => Promise.resolve({ data: { user: null, session: null }, error: { message: 'Supabase not initialized' } }),
      signOut: () => Promise.resolve({ error: null }),
      resetPasswordForEmail: () => Promise.resolve({ data: {}, error: { message: 'Supabase not initialized' } }),
      updateUser: () => Promise.resolve({ data: { user: null }, error: { message: 'Supabase not initialized' } })
    },
    storage: { 
      from: () => ({ 
        upload: () => Promise.resolve({ error: { message: 'Supabase not initialized' } }), 
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
        createSignedUrl: () => Promise.resolve({ data: { signedUrl: '' }, error: { message: 'Supabase not initialized' } })
      }) 
    }
  } as any as SupabaseClient;
} else {
  // Create Supabase client with robust session handling
  supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'impact-anchor-auth-token'
    }
  });

  // Set up a periodic token refresh check (every 10 minutes)
  setInterval(async () => {
    try {
      const { data } = await supabaseClient.auth.getSession();
      if (data?.session) {
        const expiresAt = data.session.expires_at;
        if (expiresAt) {
          const expiresInSeconds = expiresAt - Math.floor(Date.now() / 1000);
          if (expiresInSeconds < 1800) { // 30 minutes
            await supabaseClient.auth.refreshSession();
          }
        }
      }
    } catch (err) {
      // Silently handle refresh errors to avoid cluttering console
    }
  }, 10 * 60 * 1000);
}

export const supabase = supabaseClient;
